import React, { useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import ClickableIcon from '~components/ClickableIcon';
import Icon, { faChevronDown, faChevronUp, faEye, faEyeSlash } from '~components/icon';
import TermTooltip from '~components/TermTooltip';

import Pill from '../pill/Pill';

export interface BreakdownItem {
  label: string;
  value: string;
}

export interface StatisticPill {
  label?: string;
  color?: string;
  icon?: React.ReactNode;
}

export interface StatisticCaption {
  /** Unique on the page: becomes the DOM id `term-tooltip-statistic-{id}`. */
  id: string;
  /** What the title means, shown on hover and keyboard focus of the underlined title. */
  content: React.ReactNode;
}

interface ComponentProps {
  title: string;
  value?: string | number | null;
  icon?: IconProp;
  iconColor?: string;
  /** One pill, or several shown side by side (wrapping) under the value. */
  pill?: StatisticPill | StatisticPill[];
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  breakdown?: BreakdownItem[];
  dataCy?: string;
  /** Opt-in: the title becomes a dotted-underline term (TermTooltip) that explains itself. */
  caption?: StatisticCaption;
  /** Set false to remove the show/hide eye icon. Defaults to true so existing callers are unchanged. */
  allowHide?: boolean;
}

const sanitizeId = (str: string) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const Statistic: React.FC<ComponentProps> = ({
  title,
  pill,
  collapsed,
  value,
  icon,
  iconColor,
  onToggleCollapse,
  breakdown,
  dataCy,
  caption,
  allowHide = true,
}) => {
  const safeId = sanitizeId(title);
  const [expanded, setExpanded] = useState(false);
  const hasBreakdown = breakdown && breakdown.length > 0;
  const pills = pill ? ([] as StatisticPill[]).concat(pill) : [];
  const renderPill = ({ label, color, icon: pillIcon }: StatisticPill, key?: React.Key) => (
    <Pill key={key} color={color ?? '#000000'} text={label} icon={pillIcon} className="statistic-pill" />
  );

  return (
    <div className="statistic" data-cy={dataCy}>
      <div className="statistic-header">
        <h3 className="statistic-title">
          {caption ? (
            <TermTooltip
              id={`statistic-${caption.id}`}
              label={title}
              content={caption.content}
              dataCy="statistic-caption"
            />
          ) : (
            title
          )}
        </h3>
        {allowHide && (
          <ClickableIcon
            id={`hide-${safeId}`}
            icon={collapsed ? faEye : faEyeSlash}
            size="x-small"
            shape="round"
            toolTip={collapsed ? 'Show' : 'Hide'}
            onClick={onToggleCollapse}
            color="grey"
            dataCy="statistic-hide"
          />
        )}
      </div>
      <div className="statistic-content">
        {!collapsed && <div className="statistic-value">{value}</div>}
        {!collapsed && pills.length === 1 && renderPill(pills[0])}
        {!collapsed && pills.length > 1 && (
          <div className="statistic-pills">{pills.map((item, idx) => renderPill(item, item.label ?? idx))}</div>
        )}
      </div>
      {icon && (
        <div className="statistic-icon">
          <Icon icon={icon} style={iconColor ? { color: iconColor } : undefined} />
        </div>
      )}
      {!collapsed && hasBreakdown && (
        <div className="statistic-breakdown-toggle">
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${title} breakdown` : `Expand ${title} breakdown`}
            data-cy={dataCy ? `${dataCy}-expand-btn` : undefined}
          >
            <Icon icon={expanded ? faChevronUp : faChevronDown} />
          </button>
        </div>
      )}
      {!collapsed && expanded && hasBreakdown && (
        <div className="statistic-breakdown">
          {breakdown.map((item) => (
            <div key={item.label} className="statistic-breakdown-item">
              <span className="statistic-breakdown-label">{item.label}</span>
              <span className="statistic-breakdown-value">{item.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Statistic;
